/**
 * Pure textboard layout math (`canvas-textboard-slides`) — no fabric, no Angular.
 *
 * Mirrors the SVG textboard's layout intent (`SvgTextBoard` / `calculateTextboardLayout` in
 * `ui-svg-image`) after the develop-merge redesign: body text is 38px on a fixed 40px line grid
 * with a 16px gap between paragraphs, and the usable text area is defined by four edge offsets —
 * left/top are fixed (slide padding + indent, header band), right/bottom are brand-configurable
 * (PCP reserves a wide right column; TUT runs full width but stops above the paging buttons).
 * The headline keeps the original 45px band styling.
 *
 * Wrap points come from fabric text measurement in these logical 1280×720 units; cross-client
 * determinism holds because every client of a brand feeds identical font + offset inputs.
 */

/** Body text font size (px, logical units) — fixed; no auto-fit. */
export const TEXTBOARD_FONT_SIZE = 38;
/** Body line height (px) — fixed grid, decoupled from font size (develop redesign). */
export const TEXTBOARD_LINE_HEIGHT = 40;
/** Extra space between paragraphs, in line-height units (16px / 40px line = 0.4). */
export const PARAGRAPH_GAP_LINES = 16 / TEXTBOARD_LINE_HEIGHT;

/** Headline font size (px) — kept at the original 45 (only the body shrank to 38). */
export const HEADLINE_FONT_SIZE = 45;
/** Headline line height (px) = 45 × 1.2. */
export const HEADLINE_LINE_HEIGHT = 54;

/** Bullet column x (slide `TEXT_PADDING`). */
export const TEXTBOARD_BULLET_X = 30;
/** Paragraph text x = 30 padding + 51 bullet indent (= layout `offsetLeft`). */
export const TEXTBOARD_TEXT_X = 81;
/** Top of the content band = 136 header + 30 padding (= layout `offsetTop`); line n's box top = 166 + 40·(n−1). */
export const TEXTBOARD_CONTENT_TOP = 166;

/** Logical design viewport the offsets are measured in. */
const VIEWPORT_WIDTH = 1280;
const VIEWPORT_HEIGHT = 720;

/** The bullet glyph (the SVG path's `&#9724;`). */
export const TEXTBOARD_BULLET = '◼';

/** Header band geometry (the yellow band baked into the board template image). */
export const HEADLINE_BAND_HEIGHT = 136;
const HEADLINE_BAND_PADDING_TOP = 9;
/** Headline text x = 3·30 left padding. */
export const HEADLINE_TEXT_X = 90;
/** Headline wrap width = 1280 − 90 − 30. */
export const HEADLINE_WRAP_WIDTH = 1160;

/** Paging button geometry (bottom-right; unchanged by the redesign). */
export const PAGING_BUTTON_SIZE = 45;
export const PAGING_NEXT_POSITION = { x: 1210, y: 650 } as const;
export const PAGING_PREV_POSITION = { x: 1127, y: 650 } as const;

/**
 * Brand-configurable right/bottom edge offsets of the text render area (px, logical units).
 * Left/top are fixed ({@link TEXTBOARD_TEXT_X} / {@link TEXTBOARD_CONTENT_TOP}); these two come from
 * the brand config (parity with develop's `SvgTextboardConfig.layout`).
 */
export interface TextboardAreaOffsets {
    /** Distance from the right viewport edge to the rightmost text pixel (PCP 400 / TUT 20). */
    offsetRight: number;
    /** Distance from the bottom viewport edge to the bottom of the text area (PCP 30 / TUT 100). */
    offsetBottom: number;
}

export interface TextboardArea {
    /** Wrap width for the body Textboxes. */
    wrapWidth: number;
    /** Usable text height. */
    availableHeight: number;
    /** Page capacity in line-height units = floor(availableHeight / lineHeight). */
    pageLineCapacity: number;
}

/**
 * Resolve the body text area from the brand offsets (parity with `calculateTextboardLayout`).
 * A page holds `floor(availableHeight / lineHeight)` line units; each paragraph row spends 1 unit
 * and each inter-paragraph gap spends {@link PARAGRAPH_GAP_LINES}.
 */
export function computeTextboardArea(offsets: TextboardAreaOffsets): TextboardArea {
    const wrapWidth = VIEWPORT_WIDTH - TEXTBOARD_TEXT_X - offsets.offsetRight;
    const availableHeight = VIEWPORT_HEIGHT - TEXTBOARD_CONTENT_TOP - offsets.offsetBottom;
    const pageLineCapacity = Math.floor(availableHeight / TEXTBOARD_LINE_HEIGHT);
    return { wrapWidth, availableHeight, pageLineCapacity };
}

/**
 * fabric.Text multiplies `fontSize × lineHeight` by an internal `_fontSizeMult` (1.13 in fabric
 * 6.9.x) to get the rendered line-box height — so a naive `lineHeightPx / fontSizePx` ratio renders
 * lines ~13% too far apart. Divide that factor out so the fabric `lineHeight` ratio yields EXACTLY
 * `lineHeightPx` per line, putting canvas text on the same fixed 40px grid as the SVG textboard
 * (which advances its baseline cursor by an exact `lineHeight` px). Without this, multi-line
 * paragraphs are too tall, eat the inter-paragraph gap, and can force premature page breaks.
 */
const FABRIC_FONT_SIZE_MULT = 1.13;
export function fabricLineHeight(lineHeightPx: number, fontSizePx: number): number {
    return lineHeightPx / (fontSizePx * FABRIC_FONT_SIZE_MULT);
}

/**
 * Convert an absolute CSS `letter-spacing` (px) to fabric's `charSpacing`, which is expressed in
 * 1/1000 of an em (of the text object's own font size): px added per gap = `charSpacing/1000 ×
 * fontSize`. CSS letter-spacing is absolute, so the SAME px yields a DIFFERENT charSpacing per font
 * size (body 38 vs headline 45). Applying it is what makes the canvas measure/wrap like the SVG
 * textboard, whose `<text>` inherits the app's `body { letter-spacing }` (fabric's `measureText`
 * otherwise uses zero spacing → text measures narrower → breaks one word too late).
 */
export function letterSpacingToCharSpacing(letterSpacingPx: number, fontSizePx: number): number {
    return fontSizePx > 0 ? (letterSpacingPx / fontSizePx) * 1000 : 0;
}

/**
 * Caption preprocessing: remove `--` markers — parity with the SVG path's `lineBreaks(caption, false)`.
 * `<br/>` handling is deliberately absent (dead in production data).
 */
export function stripLineBreakMarkers(caption: string): string {
    return caption.replace(/--/g, '');
}

/** Where a paragraph landed: its page and its first line cursor (1-based, fractional) on that page. */
export interface ParagraphPlacement {
    page: number;
    startLine: number;
}

/**
 * Cumulative page assignment (semantics identical to `SvgTextBoard.appendText` after the redesign):
 * a paragraph whose wrapped lines do not fit the remaining page budget moves WHOLE to a new page
 * (paragraphs never split; one longer than a page overflows it). After each paragraph the line
 * cursor advances by its row count PLUS {@link PARAGRAPH_GAP_LINES}, so a multi-paragraph page holds
 * fewer paragraph-lines than its raw capacity. The gap before a page break is discarded (cursor
 * resets to 1). Pure and deterministic: same row counts + capacity → same placements everywhere.
 */
export function assignPages(rowCounts: readonly number[], pageLineCapacity: number): ParagraphPlacement[] {
    let page = 1;
    let cursor = 1;
    return rowCounts.map(rows => {
        if (cursor - 1 + rows > pageLineCapacity) {
            page++;
            cursor = 1;
        }
        const placement = { page, startLine: cursor };
        cursor += rows + PARAGRAPH_GAP_LINES;
        return placement;
    });
}

/** Highest assigned page (1 for an empty board). */
export function pageCountOf(placements: readonly ParagraphPlacement[]): number {
    return placements.reduce((max, p) => Math.max(max, p.page), 1);
}

/** Top of a paragraph whose (page-relative) first-line cursor is `startLine`. */
export function paragraphTop(startLine: number): number {
    return TEXTBOARD_CONTENT_TOP + TEXTBOARD_LINE_HEIGHT * (startLine - 1);
}

/**
 * Clamp wrapped headline lines to at most 2, hard-ellipsizing the second line (parity with
 * `SvgHeadline.textPosition`: cut 3 chars, append `...`).
 */
export function clampToTwoLines(lines: readonly string[]): { text: string; lineCount: number } {
    if (lines.length <= 2) return { text: lines.join('\n'), lineCount: Math.max(lines.length, 1) };
    const second = lines[1];
    return { text: `${lines[0]}\n${second.slice(0, Math.max(0, second.length - 3))}...`, lineCount: 2 };
}

/**
 * Top (line-box top of the first line) for a middle-aligned headline of `lineCount` lines in the
 * header band — the SVG `calculateTextPosition` result, using the headline's own 45/54 metrics.
 */
export function headlineTop(lineCount: number): number {
    const lineHeightAlign = HEADLINE_LINE_HEIGHT - HEADLINE_FONT_SIZE;
    const headerMiddle = (HEADLINE_BAND_HEIGHT + HEADLINE_BAND_PADDING_TOP) / 2;
    return headerMiddle - (lineCount * HEADLINE_LINE_HEIGHT + lineHeightAlign) / 2;
}
